import React, { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { once } from 'lodash-es';

import { Helmet } from './helmet';
import { Header } from 'components/header';
import { Footer } from './footer';
import { NavigationLoaderDecorated } from 'components/loaders/navigation';
import { getContainerWideSelector, getFooterElements } from 'common/selectors';
import { integrationSelector } from 'common/selectors/integration';
import { isIframe } from 'integration/integration.utils';
import { ContentMask } from 'components/header/content-mask';
import { AdminNavigation } from 'pages/administration.page/admin-navigation';
import { UitDownloadVideoDialog } from './fragments/uit-download-video-dialog';
import { ReduxNotification } from 'components/redux-notification';
import { ReportVideoDialog } from 'components/report-video';
import { WebcamRecordingDialogDecorated } from 'components/web-recording';
import { CancelRecordingDialog } from 'components/web-recording/cancel-recording-dialog';
import { RecordAgainConfirmationDialog } from '../../components/web-recording/confirm-record-again-dialog';
import { TermsAndConditionsPopup } from 'components/terms-and-conditions';
import { termsAndConditionsSelector } from 'common/selectors/termsAndConditions';
import { SkipToContent } from 'components/skip-to-content';
import { useAnchorTag } from './use-anchor-tag';

const addStyleString = (str) => {
  var node = document.createElement('style');
  node.innerHTML = str;
  document.body.appendChild(node);
};

// These styles break iframe experience
const addNotIFrameStyles = once(() => {
  addStyleString('html, body, body > div#root {height: 100%}');
  addStyleString('.ct-page {height: 100vh}');
});

export const PageDecorated = ({ children, hasAdminPage, hasVideoListArea }) => {
  const footerElements = useSelector(getFooterElements);
  const containerWide = useSelector(getContainerWideSelector);
  const integration = useSelector(integrationSelector);

  const layout = integration.layout;

  const termsAndConditionsData = useSelector(termsAndConditionsSelector);
  const renderTermsAndConditionsDialog = termsAndConditionsData && Object.keys(termsAndConditionsData).length > 0;

  useAnchorTag();

  useEffect(() => {
    if (!isIframe({ layout })) {
      addNotIFrameStyles();
    }
  }, [layout]);

  return (
    <Fragment>
      <Helmet />
      <div id="top" tabIndex={-1} className="ct-root-wrapper">
        <ToastContainer autoClose={3000} />
        <ContentMask />
        <NavigationLoaderDecorated>
          <SkipToContent hasVideoListArea={hasVideoListArea} />
          <div id="ct-page" tabIndex={-1} className="ct-page">
            <header className="ct-page__header">
              {hasAdminPage ? <AdminNavigation /> : !isIframe(integration) && <Header />}
            </header>
            <main id="page-content" tabIndex={-1} className="ct-page__content-wrapper">
              <div className={`ct-page__content${containerWide ? ' ct-page__content--wide' : ''}`}>{children}</div>
            </main>
            {!isIframe(integration) && (
              <footer id="page-footer" tabIndex={-1} className="ct-page__footer">
                <Footer containerWide={containerWide} items={!hasAdminPage ? footerElements : null} />
              </footer>
            )}
          </div>
        </NavigationLoaderDecorated>
      </div>
      <UitDownloadVideoDialog />
      <ReduxNotification />
      <ReportVideoDialog />
      <WebcamRecordingDialogDecorated />
      <CancelRecordingDialog />
      <RecordAgainConfirmationDialog />
      {renderTermsAndConditionsDialog && <TermsAndConditionsPopup />}
    </Fragment>
  );
};
